import { RoleType } from '../../models/Role'
import { type BillingCustomer, type EnterpriseBillingOption, type Plan, type Price, type PricePeriod } from '../../models/Plan'
import { type SeatPurchase } from '../../store/auth/auth'

const ASSISTANT_PRODUCT = 'assistant'

const perUnitAmount = (price?: Price): number | null => (price && price.billingScheme === 'perUnit' ? price.amount : null)

const billingOptionFor = (customer: BillingCustomer, period: PricePeriod): EnterpriseBillingOption | undefined =>
  customer.enterpriseSubscription?.enterpriseBillingConfig.package.billingOptions.find(
    (option) => option.planId === customer.subscription?.planId && option.period === period,
  )

const enterprisePrice = (customer: BillingCustomer, seatType: RoleType, period: PricePeriod): Price | undefined => {
  const option = billingOptionFor(customer, period)
  if (!option) {
    return undefined
  }
  return seatType === RoleType.SIDECAR ? option.additionalProducts.find((product) => product.type === ASSISTANT_PRODUCT)?.customPrice : option.customPrice
}

const planPrice = (plan: Plan | undefined, seatType: RoleType, period: PricePeriod): Price | undefined => {
  if (seatType === RoleType.SIDECAR) {
    return plan?.additionalProducts.find((product) => product.type === ASSISTANT_PRODUCT)?.prices.find((price) => price.period === period)
  }
  return plan?.prices.find((price) => price.period === period)
}

export const seatUnitPrice = (customer: BillingCustomer, plan: Plan | undefined, seatType: RoleType): number | null => {
  const period = customer.subscription?.period
  if (!period) {
    return null
  }
  return perUnitAmount(enterprisePrice(customer, seatType, period) ?? planPrice(plan, seatType, period))
}

export const newRecurringFee = (customer: BillingCustomer, plan: Plan | undefined, purchase: SeatPurchase): number | null => {
  const subscription = customer.subscription
  if (!subscription) {
    return null
  }

  const quantities: [RoleType, number][] = [
    [RoleType.SEAT, subscription.numberOfSeats + purchase.general],
    [RoleType.SIDECAR, (subscription.additionalProducts[ASSISTANT_PRODUCT]?.quantity ?? 0) + purchase.sidecar],
  ]

  return quantities
    .filter(([, quantity]) => quantity > 0)
    .reduce<number | null>((total, [seatType, quantity]) => {
      if (total === null) {
        return null
      }
      const unitAmount = seatUnitPrice(customer, plan, seatType)
      return unitAmount === null ? null : total + unitAmount * quantity
    }, 0)
}
